import { useMemo } from 'react';

import { desenharQr } from '@/lib/desenho-do-qr';

import { ajustarCodigo, qrNaCaixa, type DesenhoDoConjunto, type MedidaDoConjunto, type MedirTexto, type Retangulo } from './folha';

/**
 * UM CONJUNTO NA PROPORÇÃO DE VERDADE: o mesmo desenho que vai no PDF
 * (`desenharConjunto`), em milímetro no `viewBox`, com o vão de corte entre as
 * peças à vista. O código usa a mesma métrica da Helvetica Bold do PDF e
 * `textLength` com a largura medida: o que se vê aqui ocupa o mesmo espaço do
 * que a gráfica imprime.
 *
 * Preto e branco pelos dois tokens que não viram no tema escuro (`--branco` e
 * `--preto-obra`): o adesivo é papel, e papel não tem tema.
 */
export function PreviaDoConjunto({
  codigo,
  medida,
  desenho,
  medir,
  maiusculaPorCorpo,
}: {
  readonly codigo: string;
  readonly medida: MedidaDoConjunto;
  readonly desenho: DesenhoDoConjunto;
  readonly medir: MedirTexto;
  readonly maiusculaPorCorpo: number;
}) {
  const qr = useMemo(() => desenharQr(codigo), [codigo]);
  const texto = ajustarCodigo(codigo, desenho, medir, maiusculaPorCorpo);
  const d = desenho;

  const faixas = (r: Retangulo, e: number) => (
    <>
      <rect x={r.x} y={r.y} width={r.largura} height={e} className="fill-preto-obra" />
      <rect x={r.x} y={r.y + r.altura - e} width={r.largura} height={e} className="fill-preto-obra" />
      <rect x={r.x} y={r.y} width={e} height={r.altura} className="fill-preto-obra" />
      <rect x={r.x + r.largura - e} y={r.y} width={e} height={r.altura} className="fill-preto-obra" />
    </>
  );
  const desenhoDoQr = (r: Retangulo) => (
    <svg x={r.x} y={r.y} width={r.largura} height={r.altura} viewBox={`0 0 ${String(qr.lado)} ${String(qr.lado)}`} shapeRendering="crispEdges">
      <path d={qr.caminho} className="fill-preto-obra" />
    </svg>
  );

  return (
    <svg
      viewBox={`0 0 ${String(medida.largura)} ${String(medida.altura)}`}
      role="img"
      aria-label={`Prévia do conjunto de ${codigo}: o adesivo com o código e ${String(d.quadrados.length + 1)} QR`}
      className="block h-auto w-full"
    >
      <rect {...caixa(d.principal)} className="fill-branco" />
      {faixas(d.principal, d.borda)}
      <rect x={d.cantoDoQr.x} y={d.cantoDoQr.y} width={d.moldura} height={d.cantoDoQr.altura} className="fill-preto-obra" />
      <rect x={d.cantoDoQr.x} y={d.cantoDoQr.y} width={d.cantoDoQr.largura} height={d.moldura} className="fill-preto-obra" />
      {desenhoDoQr(qrNaCaixa(d.cantoDoQr, d, true))}
      <text
        x={texto.x}
        y={texto.linhaDeBase}
        fontSize={texto.tamanho}
        textLength={texto.largura}
        lengthAdjust="spacingAndGlyphs"
        fontFamily="Helvetica, Arial, sans-serif"
        fontWeight={700}
        className="fill-preto-obra"
      >
        {codigo}
      </text>
      {d.quadrados.map((q) => (
        <g key={`${String(q.x)}-${String(q.y)}`}>
          <rect {...caixa(q)} className="fill-branco" />
          {faixas(q, d.borda)}
          {desenhoDoQr(qrNaCaixa(q, d, false))}
        </g>
      ))}
    </svg>
  );
}

function caixa(r: Retangulo) {
  return { x: r.x, y: r.y, width: r.largura, height: r.altura };
}
